// Client portal: credit-monitoring login card. When the last report pull
// needs the client (wrong password, MyFreeScoreNow code, lapsed subscription)
// it says so and lets them fix it: update the login, or tap "Check again" —
// either one re-runs the pull.
import { useEffect, useState } from "react";
import { request } from "../lib/api";

interface Monitoring {
  provider: string | null; provider_label: string | null; username: string | null; has_login: boolean;
  last_pull_at: string | null; last_pull_ok: boolean; needs: "update_login" | "email_code" | "reactivate" | null;
}

export function PortalMonitoringCard() {
  const [m, setM] = useState<Monitoring | null>(null);
  const [editing, setEditing] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    request<{ ok: true; monitoring: Monitoring }>("/api/portal/monitoring")
      .then((r) => { setM(r.monitoring); setUsername(r.monitoring.username || ""); if (r.monitoring.needs === "update_login") setEditing(true); })
      .catch(() => setM(null));
  }, []);

  if (!m || !m.provider) return null;

  async function submit(action: "update" | "recheck") {
    setBusy(true); setErr(null); setMsg(null);
    try {
      const r = await request<{ ok: true; message: string; pulled?: boolean }>("/api/portal/monitoring", {
        method: "POST", body: action === "update" ? { action, username, password } : { action },
      });
      setMsg(r.message); setPassword(""); setEditing(false);
      if (r.pulled) setM({ ...m!, needs: null, last_pull_ok: true, last_pull_at: new Date().toISOString() });
    } catch (e: any) { setErr(e?.message || "Something went wrong. Please try again."); } finally { setBusy(false); }
  }

  const label = m.provider_label || "credit monitoring";
  const headline =
    m.needs === "update_login" ? `Please update your ${label} login`
    : m.needs === "email_code" ? `${label} needs you to sign in once`
    : m.needs === "reactivate" ? `Your ${label} subscription needs to be reactivated`
    : `Your ${label} login`;
  const help =
    m.needs === "update_login" ? `${label} did not accept the login we have on file. Enter the email and password you use to sign in and we'll pull your report right away.`
    : m.needs === "email_code" ? `Sign in at ${label}, enter the 6-digit code it emailed you, and click Continue on any notice. Then tap "Check again".`
    : m.needs === "reactivate" ? `Reactivate your ${label} account, then tap "Check again" and we'll pull your report.`
    : m.last_pull_at ? `Last report pulled ${new Date(m.last_pull_at).toLocaleDateString()}.` : "We use this to pull your credit report.";

  return (
    <section className="portal-section">
      <article className="portal-card" style={m.needs ? { borderColor: "var(--ne-red)" } : undefined}>
        <div className="portal-card-heading"><h2>{headline}</h2></div>
        <p className="portal-sub on-card" style={{ marginTop: 0 }}>{help}</p>
        {editing ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, maxWidth: 420 }}>
            <label className="form-field"><span>{m.provider === "my_free_score_now" ? "Login email" : "Username or email"}</span>
              <input value={username} autoComplete="username" onChange={(e) => setUsername(e.target.value)} /></label>
            <label className="form-field"><span>Password</span>
              <input type="password" value={password} autoComplete="current-password" onChange={(e) => setPassword(e.target.value)} /></label>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="portal-btn-primary" onClick={() => submit("update")} disabled={busy || !password.trim()}>
                {busy ? "Pulling your report (up to 2 minutes)…" : "Save and pull my report"}
              </button>
              {m.needs !== "update_login" && <button className="portal-btn-ghost" onClick={() => setEditing(false)}>Cancel</button>}
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {(m.needs === "email_code" || m.needs === "reactivate") && (
              <button className="portal-btn-primary" onClick={() => submit("recheck")} disabled={busy}>{busy ? "Pulling your report (up to 2 minutes)…" : "Check again"}</button>
            )}
            <button className="portal-btn-ghost" onClick={() => setEditing(true)}>Update my login</button>
          </div>
        )}
        {msg && <p className="portal-sub on-card" style={{ marginBottom: 0 }}>{msg}</p>}
        {err && <p className="error-text" style={{ marginBottom: 0 }}>{err}</p>}
      </article>
    </section>
  );
}
